import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Stack,
  Autocomplete
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import axios from 'axios';
import dayjs from 'dayjs';
import { useToast } from '../../../../contexts/ToastContext';

const API_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * Publish crew duty roster (DGCA 7-day advance publication) with optional base filter.
 */
const PublishRosterDialog = ({
  open,
  onClose,
  displayTimezone,
  baseOptions = [],
  initialBaseCity = ''
}) => {
  const { showToast } = useToast();
  const [periodStart, setPeriodStart] = useState(dayjs().add(7, 'day').startOf('day'));
  const [periodEnd, setPeriodEnd] = useState(dayjs().add(14, 'day').endOf('day'));
  const [remarks, setRemarks] = useState('');
  const [baseCity, setBaseCity] = useState(initialBaseCity || '');
  const [submitting, setSubmitting] = useState(false);

  const options = useMemo(
    () => [...new Set((baseOptions || []).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [baseOptions]
  );

  useEffect(() => {
    if (open) {
      setPeriodStart(dayjs().add(7, 'day').startOf('day'));
      setPeriodEnd(dayjs().add(14, 'day').endOf('day'));
      setRemarks('');
      setBaseCity(initialBaseCity || '');
    }
  }, [open, initialBaseCity]);

  const handlePublish = async () => {
    if (!periodStart || !periodEnd) {
      showToast('Select period start and end', 'error');
      return;
    }
    if (!periodEnd.isAfter(periodStart)) {
      showToast('Period end must be after start', 'error');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API_URL}/ops/crew-duty-roster/publish`,
        {
          periodStart: periodStart.startOf('day').toISOString(),
          periodEnd: periodEnd.endOf('day').toISOString(),
          remarks: remarks || undefined,
          baseCity: (baseCity || '').trim() || undefined
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      showToast(
        `Roster v${res.data.version}${res.data.baseCity ? ` (${res.data.baseCity})` : ''} published`,
        'success'
      );
      onClose(true, res.data);
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to publish roster', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="sm" fullWidth>
      <DialogTitle>Publish Duty Roster</DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          DGCA requires publication at least 7 days before the first duty in the period, covering ≥7 days,
          with weekly rest clearly indicated. Published PDFs are immutable (new version for changes).
          Optional base limits the PDF to crew whose home base city matches.
          {displayTimezone ? ` Times use client timezone ${displayTimezone}.` : ''}
        </Alert>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Autocomplete
            freeSolo
            options={options}
            value={baseCity}
            onChange={(_, v) => setBaseCity(v || '')}
            onInputChange={(_, v) => setBaseCity(v || '')}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Base (city)"
                helperText="Leave empty to publish all bases. Matches crew work location city."
              />
            )}
          />
          <DatePicker
            label="Period start"
            value={periodStart}
            onChange={setPeriodStart}
            slotProps={{ textField: { fullWidth: true } }}
          />
          <DatePicker
            label="Period end"
            value={periodEnd}
            onChange={setPeriodEnd}
            slotProps={{ textField: { fullWidth: true } }}
          />
          <TextField
            label="Remarks (optional)"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            fullWidth
            multiline
            rows={2}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)} disabled={submitting}>Cancel</Button>
        <Button variant="contained" onClick={handlePublish} disabled={submitting}>
          Publish
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PublishRosterDialog;
